import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { CHANNELS, formatChannels } from '../../lib/channels.js';
import { PrinterEditModal } from './PrinterEditModal.jsx';

const TEAL = '#5DD0C8';

const STATUS_LABEL = {
  active: 'Active',
  semi_active: 'Semi-active',
  inactive: 'Inactive',
  decommissioned: 'Decommissioned',
};

const Pill = ({ children, tone = 'muted' }) => {
  const toneClass = {
    ok:    'border-status-ok/[0.3] bg-status-ok/[0.12] text-status-ok',
    warn:  'border-ink-yellow/[0.3] bg-ink-yellow/[0.12] text-ink-yellow',
    muted: 'border-border-strong text-text-tertiary',
  }[tone];
  return <span class={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${toneClass}`}>{children}</span>;
};

const InfoCard = ({ head, rows }) => (
  <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
    <p class="m-0 mb-2.5 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">{head}</p>
    <table class="w-full text-[13px]">
      <tbody>
        {rows.map(([label, value, opts = {}]) => (
          <tr key={label}>
            <td class="w-2/5 py-1 align-top text-text-tertiary">{label}</td>
            <td class={`py-1 align-top ${opts.mono ? 'font-mono' : ''} text-text-body`}>
              {value || <span class="text-text-muted">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Stat = ({ label, value, accent = TEAL }) => (
  <div class="relative overflow-hidden rounded-sm border border-border-subtle bg-bg-card p-3">
    <span class="absolute inset-y-0 left-0 w-[3px]" style={{ background: accent }} aria-hidden="true" />
    <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">{label}</p>
    <p class="m-0 mt-1 font-serif text-[24px] font-medium tracking-tighter text-text-primary">{value}</p>
  </div>
);

const CollapsibleCard = ({ title, tag, summary, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div class="mb-3 overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        class="flex w-full items-center justify-between gap-3 border-b border-border-subtle px-4 py-2.5 text-left hover:bg-bg-hover"
      >
        <div class="min-w-0 flex-1">
          <h3 class="m-0 font-serif text-[15px] font-medium text-text-primary">{title}</h3>
          {summary && <p class="m-0 mt-0.5 text-[12px] text-text-secondary">{summary}</p>}
        </div>
        <div class="flex shrink-0 items-center gap-3">
          {tag && <span class="text-[10px] font-semibold uppercase tracking-eyebrow text-brand">{tag}</span>}
          <span class={`text-text-muted transition-transform ${open ? '' : '-rotate-90'}`}>▾</span>
        </div>
      </button>
      {open && children}
    </div>
  );
};

const Placeholder = ({ title, tag, body, defaultOpen = false }) => (
  <CollapsibleCard title={title} tag={tag} defaultOpen={defaultOpen}>
    <p class="m-0 px-4 py-3 text-[13px] font-medium text-text-secondary">{body}</p>
  </CollapsibleCard>
);

const CATEGORY_LABEL = { ink: 'Inks', media: 'Media', maintenance: 'Maintenance', parts: 'Parts', global: 'Global' };
const STATUS_DOT = {
  low:    'bg-ink-magenta shadow-[0_0_0_3px_rgba(230,59,122,0.15)]',
  medium: 'bg-ink-yellow',
  high:   'bg-status-ok',
  unset:  'bg-status-info',
};
const STATUS_PILL = {
  low:    'bg-ink-magenta/[0.12] border-ink-magenta/[0.3] text-ink-magenta',
  medium: 'bg-ink-yellow/[0.12] border-ink-yellow/[0.3] text-ink-yellow',
  high:   'bg-status-ok/[0.12] border-status-ok/[0.3] text-status-ok',
  unset:  'border-border-strong text-text-tertiary',
};

const InventoryRow = ({ item, onAdjust }) => (
  <div class="flex items-center gap-3 border-b border-border-subtle px-3 py-2 last:border-b-0 hover:bg-[rgba(255,255,255,0.02)]">
    <span class={`block h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[item.stockStatus]}`} aria-hidden="true" />
    <a href={`/inventory/${item.id}`} class="min-w-0 flex-1 truncate text-[13px] font-semibold text-text-primary hover:underline">
      {item.name}
      {item.productId && <span class="ml-1.5 font-mono text-[11px] font-medium text-text-muted">· {item.productId}</span>}
    </a>
    <span class={`inline-flex shrink-0 items-center rounded-full border px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] ${STATUS_PILL[item.stockStatus]}`}>
      {item.stockStatus}
    </span>
    <p class={`m-0 w-9 shrink-0 text-right font-serif text-[18px] font-medium tracking-tighter ${item.stockCount === 0 ? 'text-ink-magenta' : 'text-text-primary'}`}>
      {item.stockCount}
    </p>
    <div class="flex shrink-0 gap-1">
      <button
        type="button"
        onClick={() => onAdjust(item.id, -1)}
        disabled={item.stockCount === 0}
        title="Consume one"
        class="flex h-6 w-6 items-center justify-center rounded border border-border-strong font-semibold text-text-body hover:bg-bg-base disabled:cursor-not-allowed disabled:opacity-30"
      >
        −
      </button>
      <button
        type="button"
        onClick={() => onAdjust(item.id, +1)}
        title="Restock one"
        class="flex h-6 w-6 items-center justify-center rounded border border-border-strong font-semibold text-text-body hover:bg-bg-base"
      >
        +
      </button>
    </div>
  </div>
);

export const PrinterDetailPage = ({ id }) => {
  const { route } = useLocation();
  const { data: printer, loading, error, refetch } = useApi(`/printers/${id}`);
  const { data: inventoryItems, refetch: refetchInventory } =
    useApi(`/inventory?printer_id=${id}&status=active`);
  const { data: openIssues } = useApi(`/maintenance/issues?printerId=${id}&status=open,in_progress,scheduled&affectsProduction=true`);
  const { data: allIssues } = useApi(`/maintenance/issues?printerId=${id}&status=open,in_progress,scheduled`);
  const { data: recentEvents } = useApi(`/maintenance/events?printerId=${id}`);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Group inventory by category, summary stats.
  const inventoryGroups = (() => {
    const map = {};
    (inventoryItems ?? []).forEach((i) => { (map[i.category] ??= []).push(i); });
    return Object.entries(map).sort();
  })();
  const inventoryStats = (inventoryItems ?? []).reduce(
    (acc, i) => { acc.total += 1; acc[i.stockStatus] += 1; return acc; },
    { total: 0, low: 0, medium: 0, high: 0, unset: 0 },
  );

  const quickAdjust = async (itemId, delta) => {
    try {
      await api.post(`/inventory/${itemId}/adjust`, { changeAmount: delta });
      refetchInventory();
    } catch (err) {
      alert(err.message ?? 'Failed to adjust');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${printer.name}"? Any mixes saved against it will be deleted too.`)) return;
    setBusy(true);
    try {
      await api.delete(`/printers/${printer.id}`);
      route('/printers');
    } catch (err) {
      alert(err.message ?? 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <div>
      <a href="/printers" class="mb-3 inline-block text-[12px] font-semibold uppercase tracking-eyebrow text-text-tertiary hover:text-text-primary">
        ← Printers
      </a>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>}

      {printer && (
        <>
          <section class="mb-6 flex items-start justify-between gap-4">
            <div class="flex items-start gap-4">
              <div
                class="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl font-serif text-2xl font-semibold"
                style={{ background: 'linear-gradient(135deg, #2A6660, #1A3D38)', color: TEAL }}
              >
                {printer.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div class="mb-1.5 flex items-center gap-2">
                  <span class="block h-2 w-2 rounded-full" style={{ background: TEAL }} />
                  <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: TEAL }}>
                    {printer.kind === 'proofer' ? 'Proofer' : 'Office'}
                  </span>
                </div>
                <h1 class="m-0 mb-2 text-[28px] leading-[1.18] tracking-tight text-text-primary">
                  {printer.name}
                </h1>
                <div class="flex flex-wrap items-center gap-2">
                  <Pill tone={printer.status === 'active' ? 'ok' : printer.status === 'semi_active' ? 'warn' : 'muted'}>
                    <span class="block h-1.5 w-1.5 rounded-full bg-current" />
                    {STATUS_LABEL[printer.status]}
                  </Pill>
                  {printer.assetIdNumber && (
                    <Pill tone="muted">Asset · {printer.assetIdNumber}</Pill>
                  )}
                </div>
              </div>
            </div>
            <div class="flex shrink-0 gap-2">
              {printer.webManagerUrl && (
                <a
                  href={printer.webManagerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-semibold"
                  style={{ borderColor: TEAL, color: TEAL }}
                >
                  Open web manager ↗
                </a>
              )}
              {isAdmin && (
                <>
                  <Button variant="secondary" onClick={() => setEditOpen(true)}>Edit</Button>
                  <Button variant="ghost" onClick={handleDelete} disabled={busy} class="text-ink-magenta hover:text-ink-magenta">
                    Delete
                  </Button>
                </>
              )}
            </div>
          </section>

          {printer.limitations && (
            <div class="mb-6 flex items-start gap-2.5 rounded-lg border border-ink-yellow/[0.25] bg-ink-yellow/[0.06] p-3 px-4">
              <span class="text-base font-bold text-ink-yellow">⚠</span>
              <div>
                <p class="m-0 text-[13px] font-semibold text-ink-yellow">{printer.limitations}</p>
                <p class="m-0 mt-0.5 text-[12px] font-medium text-text-secondary">
                  Manual note. Surfaced as a warning anywhere this printer is referenced.
                </p>
              </div>
            </div>
          )}

          {openIssues && openIssues.length > 0 && (
            <div class="mb-6 rounded-lg border border-ink-magenta/[0.3] bg-ink-magenta/[0.06] p-3 px-4">
              <p class="m-0 mb-2 text-[13px] font-semibold text-ink-magenta">
                ⚠ {openIssues.length} open issue{openIssues.length === 1 ? '' : 's'} affecting production
              </p>
              <ul class="m-0 space-y-1 pl-0">
                {openIssues.map((i) => (
                  <li key={i.id} class="list-none">
                    <a href={`/maintenance/issues/${i.id}`} class="text-[12px] font-medium text-text-body hover:underline">
                      · {i.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div class="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
            <InfoCard
              head="Network"
              rows={[
                ['IP address',  printer.ipAddress,  { mono: true }],
                ['MAC address', printer.macAddress, { mono: true }],
                ['Web manager', printer.webManagerUrl
                  ? <a href={printer.webManagerUrl} target="_blank" rel="noopener noreferrer" style={{ color: TEAL }}>{printer.webManagerUrl}</a>
                  : null],
              ]}
            />
            <InfoCard
              head="Equipment"
              rows={[
                ['Model',    printer.model],
                ['Serial',   printer.serialNumber, { mono: true }],
                ['Asset ID', printer.assetIdNumber, { mono: true }],
                ['Location', printer.location],
                ['Media',    printer.media],
                ['Acquired', printer.purchaseDate],
              ]}
            />
          </div>

          {printer.kind === 'proofer' && (printer.channels.length > 0 || printer.physicalInks.length > 0) && (
            <div class="mb-6 rounded-lg border border-border-subtle bg-bg-card">
              <div class="grid grid-cols-1 divide-border-subtle md:grid-cols-2 md:divide-x">
                {/* Channels */}
                <div class="p-4">
                  <div class="mb-2 flex items-baseline justify-between">
                    <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">
                      Mixing channels
                    </p>
                    {printer.channels.length > 0 && (
                      <span class="text-[11px] font-medium text-text-muted">
                        {formatChannels(printer.channels)}
                      </span>
                    )}
                  </div>
                  {printer.channels.length === 0 ? (
                    <p class="m-0 text-[12px] text-text-muted">None configured.</p>
                  ) : (
                    <div class="flex flex-wrap gap-1.5">
                      {printer.channels.map((code) => {
                        const ch = CHANNELS[code];
                        if (!ch) return null;
                        return (
                          <span
                            key={code}
                            class="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-base px-3 py-1 text-[11px] font-bold"
                            style={{ color: ch.color }}
                          >
                            <span class="block h-1.5 w-1.5 rounded-full" style={{ background: ch.color }} />
                            {code.toUpperCase()}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p class="m-0 mt-2 text-[11px] font-medium text-text-muted">
                    Logical signals the mixing software emits. Used by saved mix recipes.
                  </p>
                </div>

                {/* Physical inks */}
                <div class="p-4">
                  <div class="mb-2 flex items-baseline justify-between">
                    <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">
                      Physical inks
                    </p>
                    <span class="text-[11px] font-medium text-text-muted">
                      {printer.physicalInks.length} {printer.physicalInks.length === 1 ? 'cartridge' : 'cartridges'}
                    </span>
                  </div>
                  {printer.physicalInks.length === 0 ? (
                    <p class="m-0 text-[12px] text-text-muted">
                      Not recorded. Edit the printer to add.
                    </p>
                  ) : (
                    <div class="flex flex-wrap gap-1.5">
                      {printer.physicalInks.map((name) => (
                        <span
                          key={name}
                          class="inline-flex items-center rounded-full border border-border-subtle bg-bg-base px-2.5 py-1 text-[11px] font-medium text-text-body"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                  <p class="m-0 mt-2 text-[11px] font-medium text-text-muted">
                    Actual cartridges loaded. Will sync to Inventory for stock tracking.
                  </p>
                </div>
              </div>
            </div>
          )}

          {printer.kind === 'proofer' && (
            <div class="mb-6 grid grid-cols-2 gap-2">
              <Stat label="Open issues" value={openIssues?.length ?? 0} accent="#E63B7A" />
              <Stat label="Low stock"   value={inventoryStats.low} accent="#F4C430" />
            </div>
          )}

          {/* Inventory */}
          <CollapsibleCard
            title="Inventory"
            summary={
              inventoryStats.total === 0
                ? 'No items linked yet'
                : `${inventoryStats.total} ${inventoryStats.total === 1 ? 'item' : 'items'}${
                    inventoryStats.low > 0 ? ` · ${inventoryStats.low} low` : ''
                  }${inventoryStats.unset > 0 ? ` · ${inventoryStats.unset} unset` : ''}`
            }
          >
            <div class="flex justify-end border-b border-border-subtle px-4 py-2">
              <a
                href="/inventory"
                class="rounded-md border border-border-strong px-3 py-1 text-[12px] font-semibold text-text-tertiary hover:bg-bg-base hover:text-text-body"
              >
                Open in Inventory ↗
              </a>
            </div>
            {inventoryItems == null ? (
              <p class="m-0 px-4 py-3 text-[13px] text-text-tertiary">Loading…</p>
            ) : inventoryGroups.length === 0 ? (
              <p class="m-0 px-4 py-3 text-[13px] text-text-tertiary">No inventory items linked to this printer yet.</p>
            ) : (
              inventoryGroups.map(([cat, items]) => (
                <div key={cat}>
                  <p class="m-0 border-b border-border-subtle bg-bg-header px-4 py-1.5 text-[10px] font-semibold uppercase tracking-eyebrow text-text-tertiary">
                    {CATEGORY_LABEL[cat] ?? cat} · {items.length}
                  </p>
                  {items
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((item) => (
                      <InventoryRow key={item.id} item={item} onAdjust={quickAdjust} />
                    ))}
                </div>
              ))
            )}
          </CollapsibleCard>
          {/* Issues & maintenance — live data */}
          <CollapsibleCard
            title="Issues & maintenance"
            summary={
              `${allIssues?.length ?? 0} open issue${(allIssues?.length ?? 0) === 1 ? '' : 's'}` +
              ` · ${recentEvents?.length ?? 0} event${(recentEvents?.length ?? 0) === 1 ? '' : 's'} logged`
            }
            defaultOpen={false}
          >
            <div class="flex justify-end gap-2 border-b border-border-subtle px-4 py-2">
              <a href={`/maintenance?tab=issues`}
                class="rounded-md border border-border-strong px-3 py-1 text-[12px] font-semibold text-text-tertiary hover:bg-bg-base hover:text-text-body">
                Open in Maintenance ↗
              </a>
            </div>
            <div class="px-4 py-2">
              <p class="m-0 mb-1 text-[10px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Open issues</p>
              {allIssues == null && <p class="text-[13px] text-text-tertiary">Loading…</p>}
              {allIssues && allIssues.length === 0 && <p class="m-0 text-[13px] text-text-tertiary">None open.</p>}
              {allIssues && allIssues.length > 0 && (
                <div class="space-y-1">
                  {allIssues.map((i) => (
                    <a key={i.id} href={`/maintenance/issues/${i.id}`}
                      class="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-base px-3 py-1.5 text-[13px] hover:bg-bg-hover">
                      <span class="font-mono text-[10px] text-text-muted">#{String(i.id).padStart(4, '0')}</span>
                      <span class="flex-1 truncate text-text-body">{i.title}</span>
                      {i.affectsProduction && (
                        <span class="rounded-full border border-ink-magenta/[0.3] bg-ink-magenta/[0.12] px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.04em] text-ink-magenta">Affects prod</span>
                      )}
                      <span class="text-[10px] uppercase text-text-tertiary">{i.status.replace('_', ' ')}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div class="border-t border-border-subtle px-4 py-2">
              <p class="m-0 mb-1 text-[10px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Recent maintenance</p>
              {recentEvents == null && <p class="text-[13px] text-text-tertiary">Loading…</p>}
              {recentEvents && recentEvents.length === 0 && <p class="m-0 text-[13px] text-text-tertiary">No events logged yet.</p>}
              {recentEvents && recentEvents.length > 0 && (
                <div class="space-y-1">
                  {recentEvents.slice(0, 5).map((e) => (
                    <a key={e.id} href={`/maintenance/events/${e.id}`}
                      class="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-base px-3 py-1.5 text-[13px] hover:bg-bg-hover">
                      <span class="font-mono text-[10px] text-text-muted">#{String(e.id).padStart(4, '0')}</span>
                      <span class="flex-1 truncate text-text-body">{e.title}</span>
                      <span class="text-[10px] uppercase text-text-tertiary">{e.kind}</span>
                      <span class="text-[10px] uppercase text-text-tertiary">{e.status}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleCard>
          <Placeholder
            title="Live status from web manager"
            tag="Coming when on-network"
            body="Reachability ping, toner / paper levels via SNMP, last seen timestamp. Requires testing on the proofing-room LAN — see PLANNING.md backlog."
          />

          {printer.notes && (
            <div class="mt-6 rounded-lg border border-border-subtle bg-bg-card p-4">
              <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Notes</p>
              <p class="m-0 whitespace-pre-wrap text-[13px] font-medium text-text-body">{printer.notes}</p>
            </div>
          )}

          <PrinterEditModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            onSaved={() => { setEditOpen(false); refetch(); }}
            printer={printer}
          />
        </>
      )}
    </div>
  );
};
